import { createContext, useEffect, useContext, useState } from "react";

import { UserContext } from "./user.context";

import {
  listTemplatesRequest,
  createTemplateRequest,
  updateTemplateRequest,
  deleteTemplateRequest,
} from "../../api/template.api";

export const TemplateContext = createContext(null);
export const TemplateProvider = ({ children }) => {
  const {
    user,
    currentUser,
    authUser: contextAuthUser,
  } = useContext(UserContext);

  // TemplateContext owns access to the logged-in user.
  const authUser = contextAuthUser || user || currentUser;
  const uid = authUser?.uid;

  const [templates, setTemplates] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState("");

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        if (!uid) {
          setTemplates([]);
          return;
        }

        setTemplateLoading(true);
        setTemplateError("");

        const templatesFromBackend = await listTemplatesRequest({
          uid,
        });

        setTemplates(templatesFromBackend);
      } catch (error) {
        console.error("fetchTemplates error:", {
          status: error?.response?.status,
          data: error?.response?.data,
          message: error.message,
        });

        setTemplateError("Failed to load templates.");
      } finally {
        setTemplateLoading(false);
      }
    };

    fetchTemplates();
  }, [uid]);

  const refreshTemplates = async () => {
    try {
      if (!uid) {
        setTemplates([]);
        return [];
      }

      setTemplateLoading(true);
      setTemplateError("");

      const templatesFromBackend = await listTemplatesRequest({
        uid,
      });

      setTemplates(templatesFromBackend);

      return templatesFromBackend;
    } catch (error) {
      console.error("refreshTemplates error:", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error.message,
      });

      setTemplateError("Failed to load templates.");
      return [];
    } finally {
      setTemplateLoading(false);
    }
  };

  const addTemplate = async (templatePayload) => {
    try {
      if (!uid) {
        throw new Error("No authenticated user found.");
      }

      setTemplateError("");

      const newTemplatePayload = {
        uid,
        name: templatePayload.name,
        description: templatePayload.description || "",
        tag: templatePayload.tag || "",
        tasks: templatePayload.tasks || [],
      };

      // console.log("TEMPLATE CONTEXT - CREATE PAYLOAD:", newTemplatePayload);

      const createdTemplate = await createTemplateRequest({
        template: newTemplatePayload,
      });

      setTemplates((currentTemplates) => [
        createdTemplate,
        ...currentTemplates,
      ]);

      return createdTemplate;
    } catch (error) {
      console.error("addTemplate error:", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error.message,
      });

      setTemplateError("Failed to create template.");
      throw error;
    }
  };

  const editTemplate = async ({ templateId, templatePayload }) => {
    try {
      if (!uid) {
        throw new Error("No authenticated user found.");
      }

      setTemplateError("");

      const updatedTemplatePayload = {
        uid,
        name: templatePayload.name,
        description: templatePayload.description || "",
        tag: templatePayload.tag || "",
        tasks: templatePayload.tasks || [],
      };

      console.log("TEMPLATE CONTEXT - UPDATE PAYLOAD:", {
        templateId,
        template: updatedTemplatePayload,
      });

      const updatedTemplate = await updateTemplateRequest({
        templateId,
        template: updatedTemplatePayload,
      });

      setTemplates((currentTemplates) =>
        currentTemplates.map((template) =>
          template.id === templateId || template.templateID === templateId
            ? updatedTemplate
            : template,
        ),
      );

      return updatedTemplate;
    } catch (error) {
      console.error("editTemplate error:", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error.message,
      });

      setTemplateError("Failed to update template.");
      throw error;
    }
  };

  const removeTemplate = async (templateId) => {
    try {
      if (!uid) {
        throw new Error("No authenticated user found.");
      }

      setTemplateError("");

      // console.log("TEMPLATE CONTEXT - DELETE TEMPLATE:", {
      //   uid,
      //   templateId,
      // });

      const deletedTemplate = await deleteTemplateRequest({
        uid,
        templateId,
      });

      setTemplates((currentTemplates) =>
        currentTemplates.filter(
          (template) =>
            template.id !== templateId && template.templateID !== templateId,
        ),
      );

      return deletedTemplate;
    } catch (error) {
      console.error("removeTemplate error:", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error.message,
      });

      setTemplateError("Failed to delete template.");
      throw error;
    }
  };

  const value = {
    templates,
    setTemplates,

    templateLoading,
    templateError,
    setTemplateError,

    uid,
    authUser,

    refreshTemplates,
    addTemplate,
    editTemplate,
    removeTemplate,
  };

  return (
    <TemplateContext.Provider value={value}>
      {children}
    </TemplateContext.Provider>
  );
};
